import React, { useRef, useCallback } from 'react';
import { Box } from '@mui/material';

const TOTAL_PAPER_LINES = 40;

// Convierte la sintaxis markdown simplificada a HTML
const renderMarkdown = (text) => {
  if (!text) return '';

  // Escapar HTML primero para evitar XSS
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Procesar línea por línea para manejar viñetas correctamente
  const lines = escaped.split('\n');
  let inList = false;
  const result = [];

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];

    // Aplicar negrita: *texto*
    line = line.replace(/\*([^*]+)\*/g, '<strong>$1</strong>');

    // Aplicar subrayado: _texto_
    line = line.replace(/_([^_]+)_/g, '<u>$1</u>');

    // Detectar viñeta: línea que empieza con "- "
    const isBullet = /^- (.+)/.test(line);

    if (isBullet) {
      if (!inList) {
        result.push('<ul style="margin:0; padding-left:20px; list-style-type:disc;">');
        inList = true;
      }
      const content = line.replace(/^- /, '');
      result.push(`<li style="line-height:20.8px">${content}</li>`);
    } else {
      if (inList) {
        result.push('</ul>');
        inList = false;
      }
      result.push(`<span style="display:block; line-height:20.8px; min-height:20.8px">${line}</span>`);
    }
  }

  // Cerrar lista si el texto termina en viñeta
  if (inList) result.push('</ul>');

  return result.join('');
};

const SHARED_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '13px',
  lineHeight: '20.8px',
  padding: '15px 20px',
  boxSizing: 'border-box',
  margin: 0,
  border: 'none',
  outline: 'none',
};

const LineNumberedTextarea = ({ value, onChange, placeholder, readOnly = false }) => {
  const textareaRef = useRef(null);
  const numbersRef = useRef(null);

  const handleScroll = useCallback(() => {
    if (numbersRef.current && textareaRef.current) {
      numbersRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        border: '1px solid #ccc',
        borderRadius: '4px',
        height: '800px',
        width: '100%',
        margin: '0 auto',
        bgcolor: 'white',
        boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
        overflow: 'hidden',
      }}
    >
      {/* COLUMNA DE NÚMEROS */}
      <Box
        ref={numbersRef}
        sx={{
          width: '45px',
          bgcolor: '#f8f9fa',
          borderRight: '1px solid #eee',
          flexShrink: 0,
          overflowY: 'hidden',
          userSelect: 'none',
          pt: '15px',
        }}
      >
        {Array.from({ length: TOTAL_PAPER_LINES }).map((_, i) => (
          <Box
            key={i}
            sx={{
              height: '20.8px',
              lineHeight: '20.8px',
              textAlign: 'center',
              fontSize: '13px',
              fontFamily: '"Courier New", Courier, monospace',
              color: 'grey.400',
            }}
          >
            {i + 1}
          </Box>
        ))}
      </Box>

      {/* ÁREA DE CONTENIDO */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {readOnly ? (
          // MODO LECTURA: renderiza negritas, subrayados y viñetas
          <Box
            dangerouslySetInnerHTML={{
              __html: value
                ? renderMarkdown(value)
                : `<span style="color:rgba(0,0,0,0.38)">${placeholder || ''}</span>`,
            }}
            sx={{
              ...SHARED_STYLE,
              height: '100%',
              overflowY: 'auto',
              wordWrap: 'break-word',
              color: 'rgba(0,0,0,0.87)',
              '& strong': { fontWeight: 700 },
              '& u': { textDecoration: 'underline' },
              '& ul': { margin: 0, paddingLeft: '20px' },
              '& li': { lineHeight: '20.8px' },
            }}
          />
        ) : (
          // MODO EDICIÓN: textarea limpio con hint de sintaxis en placeholder
          <textarea
            ref={textareaRef}
            value={value}
            onChange={onChange}
            onScroll={handleScroll}
            placeholder={placeholder || '*negrita*   _subrayado_   - viñeta'}
            spellCheck={false}
            style={{
              ...SHARED_STYLE,
              width: '100%',
              height: '100%',
              resize: 'none',
              background: 'transparent',
              color: 'rgba(0,0,0,0.87)',
              overflowY: 'auto',
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default LineNumberedTextarea;